type TourNotificationType = string;
type TourNotificationSubtype = string;

export type Size = 'sm' | 'lg';

export interface Insight {
  section: string;
  label: string;
  payload?: string;
  outcome?: 'above' | 'below';
  value?: number;
}

export type EmployeePreferencesWidget = {
  name: string;
  size: Size;
  hide: boolean;
};

export type EmployeePreferences = {
  firstName?: string;
  lastName?: string;
  preferences: QueryEmployeePreferencesData;
};

export type BaseEmployeePreferencesData = {
  id: string;
  managerGlobalId: string;
  preferredLanguage: string;
  reportType: string;
  lastAccessDate: string;
  tourCompleted: boolean;
};

export interface QueryEmployeePreferencesData
  extends BaseEmployeePreferencesData {
  widgets: EmployeePreferencesWidget[];
}

export interface MutationEmployeePreferencesData
  extends BaseEmployeePreferencesData {
  widgets: string;
}

export type UserPreferencesInput = Omit<
  MutationEmployeePreferencesData,
  'id' | 'managerGlobalId'
>;

export type UpdateUserPreferencesInput = Pick<
  MutationEmployeePreferencesData,
  'id'
> &
  Partial<Omit<MutationEmployeePreferencesData, 'id'>>;

export type UpdateUserPreferencesWidgetData = Pick<
  EmployeePreferencesWidget,
  'name'
> &
  Partial<Omit<EmployeePreferencesWidget, 'name'>>;

export interface TutorialStep {
  targetId: string;
  tooltip: {
    id: string;
    title: string;
    content: string;
    type?: TourNotificationType;
    subType?: TourNotificationSubtype;
    metadata?: Record<string, unknown>;
  };
  createdAt?: Date;
  order?: number;
}

export enum BringEmployeeStepsEnum {
  MANAGER_FORM = 'manager-form',
  EMPLOYEE_CURRENT_MANAGER_APPROVAL = 'employee-current-manager-approval',
  COMPLETE_REQUEST_DETAILS = 'complete-request-details',
  OFFER_LETTER = 'offer-letter',
  CONTRACT = 'contract',
  WORKDAY_PROCESSING = 'workday-processing',
  ZONE_REWARDS_MANAGER_APPROVAL = 'rewards-manager-approval',
  ZONE_REWARDS_DIRECTOR_APPROVAL = 'rewards-director-approval',
}

// Owned here — ~/app/types re-exports these for legacy compatibility
export enum RequestStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  DRAFT = 'DRAFT',
  CANCELED = 'CANCELED',
  SENT_BACK = 'SENT_BACK',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  AUTO_APPROVED = 'AUTO_APPROVED',
  AUTO_CANCELED = 'AUTO_CANCELED',
  AUTO_REJECTED = 'AUTO_REJECTED',
}

export enum TaskStatus {
  PENDING = 'PENDING',
  PENDING_APPROVAL = 'PENDING_APPROVAL',
  ANSWERED = 'ANSWERED',
  SENT_BACK = 'SENT_BACK',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  CANCELED = 'CANCELED',
  ERROR = 'ERROR',
  NOT_STARTED = 'NOT_STARTED',
  IN_PROGRESS = 'IN_PROGRESS',
  RESOLVED = 'RESOLVED',
  COMPLETED = 'COMPLETED',
  AUTO_APPROVED = 'AUTO_APPROVED',
}

export enum FeatureNameEnum {
  JOB_REQUISITION = 'TEAM_HUB.JOB_REQUISITION',
  FULL_REQUEST_VISIBILITY_STATUS = 'TEAM_HUB.FULL_REQUEST_VISIBILITY_STATUS',
  TEAM_HUB_NEW_HOME_PAGE = 'TEAM_HUB.NEW_HOME_PAGE',
  TEAM_HUB_NEW_SIDE_MENU = 'TEAM_HUB.NEW_SIDE_MENU',
  TEAM_HUB_ADMIN_PANEL = 'TEAM_HUB.ADMIN_PANEL',
  TEAM_HUB_ADMIN_PANEL_FEATURE_MANAGEMENT = 'TEAM_HUB.ADMIN_PANEL.FEATURE_MANAGEMENT',
  TEAM_HUB_ADMIN_PANEL_DEEP_SEARCH = 'TEAM_HUB.ADMIN_PANEL.DEEP_SEARCH',
  TEAM_HUB_ADMIN_PANEL_HOME = 'TEAM_HUB.ADMIN_PANEL.HOME',
  TEAM_HUB_ADMIN_PANEL_PROXY_ACCESS = 'TEAM_HUB.ADMIN_PANEL.PROXY_ACCESS',
  TEAM_HUB_ADMIN_PANEL_EMAIL_LOGS = 'TEAM_HUB.ADMIN_PANEL.EMAIL_LOGS',
  TEAM_HUB_ADMIN_PANEL_MANUAL_SNOW_TICKET_RETRY = 'TEAM_HUB.ADMIN_PANEL.MANUAL_SNOW_TICKET_RETRY',
  TEAM_HUB_ADMIN_PANEL_WORKDAY_RETRY = 'TEAM_HUB.ADMIN_PANEL.WORKDAY_RETRY',
  TEAM_HUB_ADMIN_PANEL_CANCEL_REQUEST = 'TEAM_HUB.ADMIN_PANEL.CANCEL_REQUEST',
  TEAM_HUB_ADMIN_PANEL_INTERNAL_ROLES = 'TEAM_HUB.ADMIN_PANEL.INTERNAL_ROLES',
  TEAM_HUB_ENGAGEMENT_PORTAL = 'TEAM_HUB.ENGAGEMENT_PORTAL',
  TEAM_HUB_DASHBOARD_PAGE = 'TEAM_HUB.DASHBOARD_PAGE',
  TEAM_HUB_FULL_TEAM_INFO_PAGE = 'TEAM_HUB.FULL_TEAM_INFO_PAGE',
  TEAM_HUB_MANAGER_ORGANIZATION = 'TEAM_HUB.MANAGER_ORGANIZATION',
  TEAM_HUB_MANAGER_ORGANIZATION_FREEZE_UNFREEZE = 'TEAM_HUB.MANAGER_ORGANIZATION.FREEZE_UNFREEZE',
  TEAM_HUB_MANAGER_ORGANIZATION_SHOW_DISABLED_HR_PROCESSES = 'TEAM_HUB.MANAGER_ORGANIZATION.SHOW_DISABLED_HR_PROCESSES',
  TEAM_HUB_REQUEST_QUESTIONS_DEPENDENCY_REQUESTER_ZONE = 'TEAM_HUB.REQUEST.QUESTIONS.DEPENDENCY.REQUESTER_ZONE',
  TEAM_HUB_REQUEST_OFFER_LETTER_TRANSLATION = 'TEAM_HUB.REQUEST_OFFER_LETTER.TRANSLATION',
  TEAM_HUB_REQUEST_FILES_DMS_UPLOAD = 'TEAM_HUB.REQUEST_FILES.DMS_UPLOAD',
  TEAM_HUB_REQUEST_FILES_DISABLE_PEOPLE_PLATFORM_UPLOAD = 'TEAM_HUB.REQUEST_FILES.DISABLE_PEOPLE_PLATFORM_UPLOAD',
  TEAM_HUB_PBS_SNOW_TICKET_CANCELATION = 'TEAM_HUB.PBS_SNOW_TICKET_CANCELATION',
  TEAM_HUB_PBP_EDIT_TERMINATION_ON_APPROVAL_STEP = 'TEAM_HUB.PBP_EDIT_TERMINATION_ON_APPROVAL_STEP',
  TEAM_HUB_TEAM_SUMMARY = 'TEAM_HUB.TEAM_SUMMARY',
  TEAM_HUB_CSAT_EVENTS = 'TEAM_HUB.CSAT_EVENTS',
  TEAM_HUB_CREATE_POSITION_SUBSTITUTE_EXISTING = 'TEAM_HUB.CREATE_POSITION.SUBSTITUTE_EXISTING',
  // Job Profile Guidance: "AI Assist" button + suggest-by-description drawer.
  TEAM_HUB_JOB_PROFILE_AI_ASSIST = 'TEAM_HUB.JOB_PROFILE.AI_ASSIST',
  // Job Profile Guidance: top suggestions in the dropdown while the field is empty.
  TEAM_HUB_JOB_PROFILE_TOP_SUGGESTIONS = 'TEAM_HUB.JOB_PROFILE.TOP_SUGGESTIONS',
  TEAM_HUB_REQUESTS_APPROVAL_HISTORY = 'TEAM_HUB.REQUESTS.APPROVAL_HISTORY',
  TEAM_HUB_POSITION_OVERLAP_CARD = 'TEAM_HUB.POSITION_OVERLAP_CARD',
  TEAM_HUB_AVAILABLE_FOR_OVERLAP = 'TEAM_HUB.AVAILABLE_FOR_OVERLAP',
  TEAM_HUB_ALERT_NOTICE_BAR = 'TEAM_HUB.ALERT_NOTICE_BAR',
  TEAM_HUB_MOVEMENTS_EFFECTIVE_DATE_ELAPSED_TIME = 'TEAM_HUB.MOVEMENTS.EFFECTIVE_DATE_ELAPSED_TIME',
  TEAM_HUB_ROUNDING_DIFFERENCE_DISCLAIMER = 'TEAM_HUB.COMPENSATION.ROUNDING_DIFFERENCE_DISCLAIMER',
  TEAM_HUB_DISPLAY_TEAM_MANAGEMENT_TOUR = 'TEAM_HUB.DISPLAY_TEAM_MANAGEMENT_TOUR',
  MOVE_PROMOTE = 'move-promote',
  TERMINATION = 'termination',
  BRING_EMPLOYEE = 'bring-employee',
  COPY_POSITION = 'copy-position',
  TALENT_CARD = 'talent-card',
  CLOSE_POSITION = 'close-position',
  CREATE_POSITION = 'create-position',
  TERMINATION_SEVERANCE_APPROVAL = 'termination-severance-approval',
}
